import classNames from 'classnames';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { ChangeEvent, useCallback, useEffect } from 'react';
import { RRule, Weekday } from 'rrule';
import {
  Days,
  Frequency,
  datesOptions,
  daysOptions,
  frequencyOptions,
} from '@/modules/admin-dashboard/defaults/reminders.ts';
import { Select, TextInput } from 'ui';
import { TrashIcon } from 'ui/icons';
import { CreateDueRemainderRequest, ReminderMode } from 'compliance-api';

interface ReminderItemProps {
  disabled?: boolean;
  index: number;
  errors?: Record<keyof CreateDueRemainderRequest, string>;
  onChange: (index: number, value: Partial<CreateDueRemainderRequest>) => void;
  onDelete: (index: number) => void;
  remainder: CreateDueRemainderRequest;
}

const ReminderItem = ({
  index,
  disabled,
  remainder,
  onChange,
  onDelete,
  errors,
}: ReminderItemProps) => {
  const { frequency, days_before_due, is_when_overdue, stop_after, interval_days, iterations } =
    remainder;
  const mode = remainder.mode ?? ReminderMode.Recurring;
  const isFixedInterval = is_when_overdue && mode === ReminderMode.FixedInterval;

  const currentRRule = RRule.fromString(
    frequency || new RRule({ freq: RRule.DAILY, interval: 1, dtstart: new Date() }).toString(),
  );
  const {
    origOptions: { freq, interval, bysetpos, byweekday, bymonthday },
  } = currentRRule;

  const adaptedFrequency = isFixedInterval
    ? Frequency.EVERY_N_DAYS
    : freq !== undefined && interval !== undefined
    ? `${interval}_${freq}`
    : Frequency.NEVER;

  const adaptedDay = `${bysetpos}_${get(byweekday, '[0].weekday')}`;

  const adaptedFrequencyOptions = is_when_overdue
    ? frequencyOptions
    : frequencyOptions.filter(
        ({ value }) => ![Frequency.NEVER, Frequency.EVERY_N_DAYS].includes(value),
      );

  useEffect(() => {
    const isNever = adaptedFrequency === Frequency.NEVER;
    if (is_when_overdue && !isFixedInterval && !isNever && stop_after === null) {
      onChange(index, { stop_after: 90 });
    }
  }, [is_when_overdue, isFixedInterval, adaptedFrequency, stop_after, index, onChange]);

  const handleChangeDaysBefore = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = +e.target.value;
      const isWeeklyFrequency = adaptedFrequency === Frequency.EVERY_WEEK;

      // Validate max values: 12 weeks or 90 days
      if (isWeeklyFrequency && inputValue > 12) return;
      if (!isWeeklyFrequency && inputValue > 90) return;

      // Convert weeks to days if frequency is "Every week"
      const daysBefore = isWeeklyFrequency ? inputValue * 7 : inputValue;

      if (daysBefore < 0) return;
      onChange(index, { days_before_due: daysBefore, stop_after: undefined });
    },
    [index, onChange, adaptedFrequency],
  );

  const handleChangeFrequency = useCallback(
    (option: Frequency | null) => {
      if (!option) return;
      if (option === Frequency.EVERY_N_DAYS) {
        onChange(index, {
          mode: ReminderMode.FixedInterval,
          days_before_due: 0,
          frequency: undefined,
          stop_after: undefined,
          interval_days: 7,
          iterations: 8,
        });
        return;
      }

      const isNever = option === Frequency.NEVER;
      const isEveryDay = option === Frequency.EVERY_DAY;
      const isEveryWeek = option === Frequency.EVERY_WEEK;

      const [interval, freq] = option.split('_');

      let newRuleOptions;

      const ruleOptions = isFixedInterval
        ? new RRule({ freq: RRule.DAILY, interval: 1, dtstart: new Date() }).origOptions
        : currentRRule.origOptions;

      if (isNever) {
        newRuleOptions = omit(ruleOptions, [
          'freq',
          'interval',
          'byhour',
          'byminute',
          'bysetpos',
          'byweekday',
        ]);
      } else if (isEveryDay) {
        newRuleOptions = omit(
          {
            ...ruleOptions,
            freq: +freq,
            interval: +interval,
          },
          ['bysetpos', 'byweekday'],
        );
      } else if (isEveryWeek) {
        newRuleOptions = omit(
          {
            ...ruleOptions,
            freq: +freq,
            interval: +interval,
            bysetpos: 1,
            byweekday: RRule.MO,
          },
          ['byhour', 'byminute'],
        );
      } else {
        newRuleOptions = omit(
          {
            ...ruleOptions,
            freq: +freq,
            interval: +interval,
            bymonthday: 1,
          },
          ['byhour', 'byminute', 'bysetpos', 'byweekday'],
        );
      }

      const newRule = new RRule(newRuleOptions);
      const value: Partial<CreateDueRemainderRequest> = {
        mode: ReminderMode.Recurring,
        frequency: newRule.toString(),
        interval_days: undefined,
        iterations: undefined,
      };

      if (!is_when_overdue) {
        value.stop_after = undefined;
      }

      onChange(index, value);
    },
    [currentRRule.origOptions, index, onChange, is_when_overdue, isFixedInterval],
  );

  const handleChangeFixedInterval = useCallback(
    (field: 'interval_days' | 'iterations') => (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.value) {
        onChange(index, { [field]: undefined });
        return;
      }

      const value = +e.target.value;
      if (value < 1 || value > 90) return;
      onChange(index, { [field]: value });
    },
    [index, onChange],
  );

  const handleChangeDate = useCallback(
    (option: number | null) => {
      if (!option) return;

      const newRule = new RRule({
        ...currentRRule.origOptions,
        bymonthday: option,
      });

      const value: Partial<CreateDueRemainderRequest> = { frequency: newRule.toString() };

      if (!is_when_overdue) {
        value.stop_after = undefined;
      }

      onChange(index, value);
    },
    [currentRRule.origOptions, index, onChange, is_when_overdue],
  );

  const handleChangeDay = useCallback(
    (option: Days | null) => {
      if (!option) return;
      const [bysetpos, byweekday] = option.split('_');

      const newRule = new RRule({
        ...currentRRule.origOptions,
        byweekday: new Weekday(+byweekday),
        bysetpos: +bysetpos,
      });

      const value: Partial<CreateDueRemainderRequest> = { frequency: newRule.toString() };

      if (!is_when_overdue) {
        value.stop_after = undefined;
      }

      onChange(index, value);
    },
    [currentRRule.origOptions, index, onChange, is_when_overdue],
  );

  // const handleChangeTime = useCallback(
  //   (e: ChangeEvent<HTMLInputElement>) => {
  //     const [hours, minutes] = e.target.value.split(':');
  //
  //     const newRule = new RRule({
  //       ...currentRRule.origOptions,
  //       byhour: +hours,
  //       byminute: +minutes,
  //     });
  //
  //     onChange(index, { frequency: newRule.toString() });
  //   },
  //   [currentRRule.origOptions, index, onChange],
  // );

  const handleChangeStopAfter = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const stopAfter = +e.target.value;

      if (stopAfter < 1 || stopAfter > 90) return;
      onChange(index, { stop_after: stopAfter });
    },
    [index, onChange],
  );

  return (
    <div className="flex flex-col gap-4">
      <div
        className={classNames('grid gap-4', {
          'grid-cols-[4fr_5fr_4fr_auto]': ![Frequency.EVERY_DAY, Frequency.NEVER].includes(
            adaptedFrequency as Frequency,
          ),
          'grid-cols-[4fr_9fr_auto]': [
            Frequency.EVERY_DAY,
            Frequency.NEVER,
            Frequency.EVERY_N_DAYS,
          ].includes(adaptedFrequency as Frequency),
        })}
      >
        {is_when_overdue ? (
          <div className="self-center">If task is Overdue</div>
        ) : (
          <TextInput
            disabled={disabled}
            onChange={handleChangeDaysBefore}
            name="days"
            label={
              adaptedFrequency === Frequency.EVERY_WEEK
                ? 'Weeks Before Due date'
                : 'Days Before Due date'
            }
            type="number"
            value={
              adaptedFrequency === Frequency.EVERY_WEEK
                ? Math.floor((days_before_due || 0) / 7)
                : days_before_due || ''
            }
            className="min-w-0 w-full"
            error={errors?.days_before_due}
            min={0}
            max={adaptedFrequency === Frequency.EVERY_WEEK ? 12 : 90}
          />
        )}
        <Select
          disabled={disabled}
          label="FREQUENCY"
          positionMenu="up"
          options={adaptedFrequencyOptions}
          value={adaptedFrequency as Frequency}
          onChange={handleChangeFrequency}
          error={errors?.frequency}
        />
        {![
          Frequency.EVERY_DAY,
          Frequency.EVERY_WEEK,
          Frequency.NEVER,
          Frequency.EVERY_N_DAYS,
        ].includes(adaptedFrequency as Frequency) && (
          <Select
            label="On day"
            positionMenu="up"
            options={datesOptions}
            value={bymonthday as number | null}
            onChange={handleChangeDate}
            isClearable={true}
            isMulti={false}
            disabled={adaptedFrequency === Frequency.NEVER || disabled}
          />
        )}
        {adaptedFrequency === Frequency.EVERY_WEEK && (
          <Select
            disabled={disabled}
            label="DAY"
            positionMenu="up"
            options={daysOptions}
            value={adaptedDay as Days}
            onChange={handleChangeDay}
          />
        )}
        {/*{adaptedFrequency === Frequency.EVERY_DAY && (*/}
        {/*  <TextInput*/}
        {/*    disabled={disabled}*/}
        {/*    name={`time_reminder_${index}`}*/}
        {/*    type="time"*/}
        {/*    className="pt-7 min-w-0 w-full"*/}
        {/*    value={`${byhour?.toString().padStart(2, '0')}:${byminute?.toString().padStart(2, '0')}`}*/}
        {/*    onChange={handleChangeTime}*/}
        {/*  />*/}
        {/*)}*/}

        <button
          type="button"
          disabled={disabled}
          onClick={onDelete.bind(this, index)}
          className={classNames({ 'opacity-0': is_when_overdue })}
        >
          <img src={TrashIcon} alt="remove" />
        </button>
      </div>
      {isFixedInterval && (
        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-2">
            <TextInput
              disabled={disabled}
              onChange={handleChangeFixedInterval('interval_days')}
              name="interval_days"
              label="Every"
              type="number"
              value={interval_days ?? ''}
              className="min-w-0 w-32"
              error={errors?.interval_days}
              min={1}
              max={90}
            />
            <span className="pb-3 text-sm text-gray-900">Days</span>
          </div>
          <div className="flex items-end gap-2">
            <TextInput
              disabled={disabled}
              onChange={handleChangeFixedInterval('iterations')}
              name="iterations"
              label="Repeat"
              type="number"
              value={iterations ?? ''}
              className="min-w-0 w-32"
              error={errors?.iterations}
              min={1}
              max={90}
            />
            <span className="pb-3 text-sm text-gray-900">Times</span>
          </div>
        </div>
      )}
      {is_when_overdue && !isFixedInterval && adaptedFrequency !== Frequency.NEVER && (
        <div className="flex items-end gap-2">
          <TextInput
            disabled={disabled}
            onChange={handleChangeStopAfter}
            name="stop_after"
            label="Stop after"
            type="number"
            value={stop_after ?? 90}
            className="min-w-0 w-32"
            error={errors?.stop_after}
            min={1}
            max={90}
          />
          <span className="pb-3 text-sm text-gray-900">Days</span>
        </div>
      )}
    </div>
  );
};

export default ReminderItem;
